import * as THREE from 'three';
import { createCoreboundRenderer } from './createCoreboundRenderer';
import { createTravelDie } from './portalTravel';
import { observeElementSize } from '../../utils/observeElementSize';
import { CORE_APPEARANCE } from './coreAppearance';

const smooth = (time, start, end) =>
  THREE.MathUtils.smoothstep(time, start, end);
const pulse = (time, at) => Math.exp(-Math.pow((time - at) / 0.24, 2));

const getLastSignalFrame = (variant, time) => {
  const arrival = variant === 'arrival';
  return {
    corruption: arrival ? 1 : smooth(time, 3.6, 10.8),
    loom: arrival ? smooth(time, 16.8, 19.2) : smooth(time, 14.8, 18.5),
    rings: arrival ? smooth(time, 5.9, 7.1) : 0,
    core: arrival ? smooth(time, 9.1, 10.6) : 0,
    pulse: arrival
      ? pulse(time, 2.8) + pulse(time, 6) + pulse(time, 9.2)
      : pulse(time, 3.6),
    signal: arrival ? smooth(time, 12.4, 13.5) : smooth(time, 10.8, 12.2),
  };
};

const luminous = (color, opacity = 1) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });

const createGalaxy = (seed, radius) => {
  const count = 3200;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  const color = new THREE.Color();
  for (let index = 0; index < count; index++) {
    const fraction = index / count;
    const distance = Math.sqrt(fraction) * radius;
    const angle =
      distance * 2.8 +
      ((index % 3) * Math.PI * 2) / 3 +
      Math.sin(index * 71.3 + seed) * 0.23;
    const scatter =
      Math.sin(index * 127.1 + seed) * Math.cos(index * 31.7) * fraction;
    positions[index * 3] = Math.cos(angle) * distance + scatter * 0.7;
    positions[index * 3 + 1] =
      Math.sin(angle) * distance * 0.45 + scatter * 0.2;
    positions[index * 3 + 2] = scatter * 0.6;
    color.setHSL(0.59 + fraction * 0.14, 0.6, 0.5 + (1 - fraction) * 0.45);
    colors.set([color.r, color.g, color.b], index * 3);
    sizes[index] = 1.4 + (index % 7) * 0.3 + (1 - fraction) * 1.2;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
  const material = new THREE.ShaderMaterial({
    uniforms: {
      life: { value: 1 },
      time: { value: 0 },
      pixelRatio: { value: 1 },
    },
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: `
      attribute float size;
      uniform float life;
      uniform float time;
      uniform float pixelRatio;
      varying vec3 vColor;
      void main() {
        vec3 p = position;
        float band = floor(p.y * 14.0);
        p.x += sin(band * 13.0 + floor(time * 9.0))
          * (1.0 - life) * 0.07;
        vec4 view = modelViewMatrix * vec4(p, 1.0);
        gl_Position = projectionMatrix * view;
        gl_PointSize = clamp(size * pixelRatio * 18.0 / -view.z, 1.0, 7.0);
        vColor = mix(vec3(0.10, 0.016, 0.16), color, life);
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      void main() {
        float glow = max(0.0, 1.0 - length(gl_PointCoord - 0.5) * 2.0);
        gl_FragColor = vec4(vColor, glow * glow * 0.85);
      }
    `,
  });
  const group = new THREE.Group();
  group.add(new THREE.Points(geometry, material));
  // Soft gas gives the pinpoints depth and lets the corruption visibly drain
  // the galaxy's color, rather than just switching off a starfield.
  const gasMaterial = new THREE.ShaderMaterial({
    uniforms: material.uniforms,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec2 vUv;
      uniform float life;
      uniform float time;
      float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float noise(vec2 p) {
        vec2 cell = floor(p), f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(cell), hash(cell + vec2(1, 0)), f.x),
          mix(hash(cell + vec2(0, 1)), hash(cell + 1.0), f.x), f.y);
      }
      void main() {
        vec2 p = (vUv - 0.5) * 3.4;
        p.y *= 2.1;
        float r = length(p);
        float angle = atan(p.y, p.x + 0.00001);
        float clouds = noise(p * 8.0) * 0.5 + noise(p * 23.0) * 0.3 + noise(p * 61.0) * 0.2;
        float arms = pow(0.5 + 0.5 * cos(angle * 3.0 - r * 10.0 + clouds * 2.5), 3.0);
        float density = (0.12 + arms * 0.75) * exp(-r * 2.3) * clouds;
        density *= 1.0 - smoothstep(0.7, 1.65, r);
        vec3 tint = mix(vec3(0.13, 0.40, 0.95), vec3(0.64, 0.20, 0.85), clouds);
        vec3 color = tint * density * 2.1;
        color += vec3(1.0, 0.80, 0.56) * exp(-r * 18.0) * 1.1;
        color *= 0.025 + life * 0.975;
        gl_FragColor = vec4(color, clamp(density * 3.0 + exp(-r * 16.0), 0.0, 1.0));
      }
    `,
  });
  const gas = new THREE.Mesh(
    new THREE.PlaneGeometry(radius * 2.4, radius * 2.4),
    gasMaterial
  );
  gas.position.z = -0.1;
  group.add(gas);
  group.userData.uniforms = material.uniforms;
  return group;
};

export const createLastSignalScene = (host, { variant, dieId, faceValues }) => {
  let renderer;
  try {
    renderer = createCoreboundRenderer({
      antialias: true,
      powerPreference: 'low-power',
    });
  } catch {
    return null;
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  host.appendChild(renderer.domElement);
  const arrival = variant === 'arrival';
  const scene = new THREE.Scene();
  scene.background = new THREE.Color('#02040b');
  const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 140);
  const ambientLight = new THREE.HemisphereLight('#bbddff', '#100a24', 1.1);
  scene.add(ambientLight);
  const keyLight = new THREE.DirectionalLight('#badfff', 2.5);
  keyLight.position.set(-4, 6, 10);
  scene.add(keyLight);
  const coreLight = new THREE.PointLight('#cf61ff', 1, 24, 1.5);
  coreLight.position.set(0, 2.6, 1);
  scene.add(coreLight);

  const starsGeometry = new THREE.BufferGeometry();
  const starPositions = new Float32Array(900 * 3);
  for (let index = 0; index < 900; index++) {
    starPositions[index * 3] = Math.sin(index * 127.1) * 32;
    starPositions[index * 3 + 1] = Math.cos(index * 97.3) * 20;
    starPositions[index * 3 + 2] = -8 - (index % 47);
  }
  starsGeometry.setAttribute(
    'position',
    new THREE.BufferAttribute(starPositions, 3)
  );
  const stars = new THREE.Points(
    starsGeometry,
    new THREE.PointsMaterial({
      color: '#b8d8ff',
      size: 0.035,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
    })
  );
  scene.add(stars);

  const galaxies = [
    [-0.5, 3.8, -7, 5.6, -0.3],
    [-8, 2.5, -12, 3.4, 0.4],
    [8, 5, -15, 4, -0.4],
    [-5, -1, -8, 2.4, 0.6],
    [6, -0.3, -10, 2.7, -0.2],
  ].map(([x, y, z, radius, tilt], index) => {
    const galaxy = createGalaxy(index * 47.2, radius);
    galaxy.position.set(x, y, z);
    galaxy.rotation.set(0.3, 0.05, tilt);
    scene.add(galaxy);
    return galaxy;
  });

  // A station silhouette establishes inhabited space before its lights fail.
  const station = new THREE.Group();
  station.position.set(-4.4, 2.5, -0.5);
  station.rotation.set(0.3, 0.5, -0.25);
  const stationMaterial = luminous('#9cdcff', 0.5);
  station.add(
    new THREE.Mesh(new THREE.TorusGeometry(0.65, 0.025, 5, 64), stationMaterial)
  );
  for (let index = 0; index < 4; index++) {
    const strut = new THREE.Mesh(
      new THREE.BoxGeometry(1.35, 0.025, 0.025),
      stationMaterial
    );
    strut.rotation.z = (index * Math.PI) / 4;
    station.add(strut);
  }
  scene.add(station);

  const shell = new THREE.Group();
  shell.position.set(
    arrival ? 0 : 5.2,
    arrival ? 3.4 : 4.4,
    arrival ? -0.5 : -7
  );
  shell.scale.setScalar(arrival ? 1.7 : 0.6);
  const shellMaterial = new THREE.MeshPhysicalMaterial({
    color: CORE_APPEARANCE.containment.color,
    emissive: CORE_APPEARANCE.containment.emissive,
    emissiveIntensity: 0.05,
    metalness: 0.6,
    roughness: 0.22,
    clearcoat: 1,
  });
  const shellGeometry = new THREE.IcosahedronGeometry(0.95, 1);
  const body = new THREE.Mesh(shellGeometry, shellMaterial);
  const wire = new THREE.LineSegments(
    new THREE.EdgesGeometry(shellGeometry, 10),
    new THREE.LineBasicMaterial({
      color: '#e8b7ff',
      transparent: true,
      opacity: 0.03,
    })
  );
  shell.add(body, wire);
  const rings = [1.48, 1.82, 2.16].map((radius, index) => {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(radius, 0.012, 6, 128),
      luminous(['#8e65ff', '#ff75c9', '#63f6ff'][index], 0)
    );
    ring.rotation.set(0.6 + index * 0.55, index * 0.65, index * 0.3);
    shell.add(ring);
    return ring;
  });
  scene.add(shell);

  const fracture = new THREE.Group();
  const fractureMaterial = luminous('#f884ff', 0);
  for (let index = 0; index < 7; index++) {
    const points = Array.from({ length: 15 }, (_, point) => {
      const x = -10 + point * 1.5;
      return new THREE.Vector3(
        x,
        3 + Math.sin(point * 2.3 + index) * 0.18 + index * 0.09,
        -2 + index * 0.06
      );
    });
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    fracture.add(new THREE.Line(geometry, fractureMaterial));
  }
  scene.add(fracture);

  const loom = new THREE.Group();
  loom.position.set(0, -1, 4.2);
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(4.7, 0.16, 4.7),
    new THREE.MeshStandardMaterial({
      color: '#071b2a',
      metalness: 0.7,
      roughness: 0.3,
    })
  );
  loom.add(base);
  const tiles = [];
  for (let index = 0; index < 25; index++) {
    const tile = new THREE.Mesh(
      new THREE.BoxGeometry(0.76, 0.025, 0.76),
      new THREE.MeshStandardMaterial({
        color: '#0b2435',
        emissive: '#6af4ef',
        emissiveIntensity: 0,
        metalness: 0.5,
        roughness: 0.2,
      })
    );
    tile.position.set(
      ((index % 5) - 2) * 0.9,
      0.12,
      (Math.floor(index / 5) - 2) * 0.9
    );
    loom.add(tile);
    tiles.push(tile);
  }
  const loomEdge = new THREE.LineSegments(
    new THREE.EdgesGeometry(base.geometry),
    new THREE.LineBasicMaterial({
      color: '#8efff4',
      transparent: true,
      opacity: 0,
    })
  );
  loom.add(loomEdge);
  scene.add(loom);
  const die = createTravelDie(dieId, faceValues);
  scene.add(die);

  const beaconMaterial = luminous('#a4fff4', 0);
  const beacon = new THREE.Mesh(
    new THREE.TorusGeometry(0.25, 0.016, 6, 64),
    beaconMaterial
  );
  beacon.position.set(arrival ? -5 : -2.2, 2.8, -1);
  scene.add(beacon);
  const signalPath = new THREE.CatmullRomCurve3([
    beacon.position.clone(),
    new THREE.Vector3(-1.5, 2.6, 1),
    new THREE.Vector3(0, 0.2, 4.2),
  ]);
  const signalThread = new THREE.Mesh(
    new THREE.TubeGeometry(signalPath, 48, 0.009, 5, false),
    luminous('#a1fff1', 0)
  );
  scene.add(signalThread);
  const spark = new THREE.Mesh(
    new THREE.SphereGeometry(0.055, 8, 8),
    luminous('#e2fff7')
  );
  scene.add(spark);
  const closingPortal = new THREE.Mesh(
    new THREE.TorusGeometry(1.3, 0.04, 8, 96),
    luminous('#c58bff', 0.7)
  );
  closingPortal.position.set(0, -0.2, 7);
  closingPortal.visible = arrival;
  scene.add(closingPortal);

  let aspect = 1,
    resized = true,
    renderedTime = null;
  const stopObserving = observeElementSize(host, ({ width, height }) => {
    aspect = width / height;
    renderer.setSize(width, height, false);
    camera.aspect = aspect;
    camera.updateProjectionMatrix();
    resized = true;
    galaxies.forEach((galaxy) => {
      galaxy.userData.uniforms.pixelRatio.value = renderer.getPixelRatio();
    });
  });
  let contextLost = false;
  const onContextLost = (event) => {
    event.preventDefault();
    contextLost = true;
  };
  renderer.domElement.addEventListener('webglcontextlost', onContextLost);
  return {
    update(time) {
      if (contextLost) return false;
      if (renderedTime === time && !resized) return true;
      renderedTime = time;
      resized = false;
      const frame = getLastSignalFrame(variant, time);
      const portrait = aspect < 0.85;
      const reframe = arrival ? frame.loom : smooth(time, 12.5, 16);
      camera.position.set(
        Math.sin(time * 0.08) * 0.25,
        4 - frame.loom * 0.5,
        (portrait ? 24 : 16) - frame.loom * 1.2
      );
      camera.lookAt(
        0,
        (portrait ? 2.6 : 1.6) - reframe * (arrival ? 0.5 : 3.2),
        arrival ? 0 : reframe * 3.4
      );
      stars.material.opacity = arrival ? 0.08 : 0.65 - frame.corruption * 0.6;
      galaxies.forEach((galaxy, index) => {
        galaxy.userData.uniforms.time.value = time;
        galaxy.userData.uniforms.life.value = arrival
          ? 0.015
          : 1 - smooth(time, 3.6 + index * 0.7, 8.3 + index * 0.7) * 0.985;
        galaxy.rotation.y = Math.sin(time * 0.04 + index) * 0.08;
      });
      station.visible = !arrival;
      stationMaterial.opacity = (1 - frame.corruption) * 0.5;
      station.rotation.z = -0.25 + time * 0.018;
      fracture.visible = !arrival;
      fractureMaterial.opacity = Math.sin(frame.corruption * Math.PI) * 0.8;
      fracture.scale.y = 0.6 + frame.corruption * 4;
      const reveal = arrival ? frame.core : 0.25 + frame.corruption * 0.5;
      shell.visible = arrival ? time >= 2.75 : time >= 3.6;
      keyLight.intensity = arrival
        ? 0.02 + frame.core * 2.5 + frame.pulse * frame.core * 2
        : 2.5;
      ambientLight.intensity = arrival ? 0.015 + frame.core * 1.1 : 1.1;
      shellMaterial.emissiveIntensity =
        0.015 +
        reveal * 1.4 +
        frame.pulse * (arrival ? 0.04 + frame.core * 0.5 : 1.1);
      wire.material.opacity =
        0.015 +
        reveal * 0.55 +
        frame.pulse * (arrival ? frame.core * 0.2 : 0.2);
      body.rotation.set(time * 0.035, time * 0.1, 0.15);
      wire.rotation.copy(body.rotation);
      rings.forEach((ring, index) => {
        ring.material.opacity = arrival
          ? frame.rings * (0.18 + frame.core * 0.25 + frame.pulse * 0.25)
          : 0.12;
        ring.rotation.z = time * (index % 2 ? -0.07 : 0.06);
        ring.rotation.y = index * 0.65 + time * 0.035;
      });
      coreLight.intensity =
        0.1 + reveal * 3 + frame.pulse * (arrival ? 0.4 + frame.core * 7.6 : 8);
      closingPortal.visible = arrival && time < 1.6;
      closingPortal.scale.setScalar(Math.max(0.001, 1 - smooth(time, 0, 1.6)));
      closingPortal.material.opacity = 0.65 * (1 - smooth(time, 0.6, 1.6));
      loom.visible = arrival || time > 12.5;
      loom.position.y = -1.4 + frame.loom * (arrival ? 1.8 : 0.6);
      tiles.forEach((tile, index) => {
        const activation = smooth(
          frame.loom,
          (index % 5) * 0.08,
          0.6 + (index % 5) * 0.08
        );
        tile.material.emissiveIntensity =
          activation * (index === 12 ? 1.8 : 0.45 + (index % 3) * 0.12);
      });
      loomEdge.material.opacity = frame.loom * 0.7;
      die.visible = arrival || time > 14;
      die.position.set(
        0,
        -0.65 + frame.loom * (arrival ? 3.2 : 1.7),
        arrival ? 7 - smooth(time, 0, 2) * 2.8 : 4.2
      );
      die.scale.setScalar(0.65 + frame.loom * 0.25);
      die.rotation.set(time * 0.1, time * 0.3, Math.sin(time * 0.3) * 0.1);
      beaconMaterial.opacity = frame.signal * (0.5 + Math.sin(time * 2) * 0.18);
      beacon.scale.setScalar(1 + (time % 1.6) * 0.4);
      signalThread.material.opacity = frame.signal * 0.3;
      spark.visible = frame.signal > 0;
      spark.position.copy(signalPath.getPoint((time * 0.3) % 1));
      renderer.render(scene, camera);
      return true;
    },
    dispose() {
      stopObserving();
      renderer.domElement.removeEventListener(
        'webglcontextlost',
        onContextLost
      );
      const geometries = new Set(),
        materials = new Set(),
        textures = new Set();
      scene.traverse((object) => {
        if (object.geometry) geometries.add(object.geometry);
        if (object.material) {
          const surfaces = Array.isArray(object.material)
            ? object.material
            : [object.material];
          surfaces.forEach((material) => {
            materials.add(material);
            if (material.map) textures.add(material.map);
          });
        }
      });
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
      textures.forEach((texture) => texture.dispose());
      renderer.dispose();
      renderer.forceContextLoss?.();
      renderer.domElement.remove();
    },
  };
};
